import { forwardRef, createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, useId, type ComponentProps, type ElementRef, type CSSProperties, type ReactNode, type HTMLAttributes, type FC } from "react"
import { type LucideIcon } from "lucide-react"

import { cn } from "@/lib/utils"

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds the app-wide hover-lift treatment used by interactive card surfaces. */
  interactive?: boolean
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-3xl border border-border/60 bg-card text-card-foreground shadow-[var(--shadow-card)]",
        interactive && "transition-all duration-200 hover:-translate-y-[2px] hover:shadow-lg",
        className
      )}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

/** Tint families for card header chips. */
export type CardIconTone =
  | "primary"
  | "sky"
  | "leaf"
  | "sun"
  | "plum"
  | "rose"
  | "warning"

/**
 * Tone → chip class map: `bg-[hsl(var(--X)/0.15)]` tint + matching icon/emoji
 * color. Derived from the children-profile header chips so those migrate
 * pixel-for-pixel (sky=chart-2, leaf=success, plum=chart-5, rose=destructive,
 * warning uses --warning-text; primary + sun=chart-4 round out the semantic set).
 * Exported so any component can reuse the exact same tinted chip.
 */
export const cardIconToneClass: Record<CardIconTone, string> = {
  primary: "bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))]",
  sky: "bg-[hsl(var(--chart-2)/0.15)] text-[hsl(var(--chart-2))]",
  leaf: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]",
  sun: "bg-[hsl(var(--chart-4)/0.15)] text-[hsl(var(--chart-4))]",
  plum: "bg-[hsl(var(--chart-5)/0.15)] text-[hsl(var(--chart-5))]",
  rose: "bg-[hsl(var(--destructive)/0.15)] text-[hsl(var(--destructive))]",
  warning: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning-text))]",
}

interface CardIconProps {
  /** Lucide icon component or emoji string. */
  icon: LucideIcon | string
  /** Tint family (default `primary`). */
  tone?: CardIconTone
  className?: string
}

/**
 * 40px tinted, rounded header chip holding a lucide icon or emoji. Rendered
 * automatically by `<CardTitle icon=… tone=… />`; exported standalone for
 * custom headers that need the same chip outside a CardTitle.
 */
const CardIcon: FC<CardIconProps> = ({ icon, tone = "primary", className }) => {
  let content: ReactNode
  if (typeof icon === "string") {
    content = icon
  } else {
    const Icon = icon
    content = <Icon className="h-5 w-5" />
  }
  return (
    <span
      className={cn(
        "grid h-10 w-10 shrink-0 place-items-center rounded-xl text-lg",
        cardIconToneClass[tone],
        className
      )}
    >
      {content}
    </span>
  )
}
CardIcon.displayName = "CardIcon"

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Lucide icon component or emoji string — renders a tinted header chip before the title. */
  icon?: LucideIcon | string
  /** Chip tint family (default `primary`). Ignored when `icon` is absent. */
  tone?: CardIconTone
}

const CardTitle = forwardRef<HTMLParagraphElement, CardTitleProps>(
  ({ className, icon, tone = "primary", ...props }, ref) => {
    // No icon → byte-identical to the original CardTitle (children flow through props).
    if (icon == null) {
      return (
        <h3
          ref={ref}
          className={cn(
            "text-2xl font-semibold leading-none tracking-tight",
            className
          )}
          {...props}
        />
      )
    }
    const { children, ...rest } = props
    return (
      <h3
        ref={ref}
        className={cn(
          "flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight",
          className
        )}
        {...rest}
      >
        <CardIcon icon={icon} tone={tone} />
        {children}
      </h3>
    )
  }
)
CardTitle.displayName = "CardTitle"

const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardIcon, CardDescription, CardContent }
